import { useQuery, useQueryClient } from 'react-query'
import { useRouter } from 'next/router'
import {
  getCommentsService,
  getRepliesService,
  getUsersService,
} from 'services/comment'
import useSession from './session'

export async function prefetchComments({
  queryClient, course, lesson_slug, token, featuredComment, req, res,
}) {
  await queryClient.prefetchQuery(
    ['GET_COMMENTS', course, lesson_slug, token, featuredComment],
    () => getCommentsService({
      course,
      lesson_slug,
      token,
      featured: featuredComment,
      req,
      res,
    }),
  )
}

export function useCommentsKey() {
  const { course, lesson_slug, featured } = useRouter().query
  const { token } = useSession()
  const featuredComment = featured || null
  return (
    ['GET_COMMENTS', course, lesson_slug, token, featuredComment]
  )
}

export function useComments() {
  const COURSE_KEY = useCommentsKey()
  const [key, course, lesson_slug, token, featured] = COURSE_KEY
  return useQuery(
    COURSE_KEY,
    () => getCommentsService({
      course,
      lesson_slug,
      token,
      featured,
    }),
    {
      enabled: !!token,
      staleTime: Infinity,
      placeholderData: {
        count: 0,
        next: null,
        previous: null,
        results: [],
      },
      refetchOnMount: false,
    },
  )
}

export function useReplies(parent) {
  const COMMENTS_KEY = useCommentsKey()
  const [key, course, lesson_slug, token, featured] = COMMENTS_KEY
  const queryClient = useQueryClient()
  return useQuery(
    ['GET_COMMENT_REPLIES', parent],
    () => getRepliesService({
      course,
      lesson_slug,
      parent,
      token,
    }),
    {
      initialData: () => {
        const comments = queryClient.getQueryData(COMMENTS_KEY)
        const parentComment = comments?.results.find(({ uuid }) => uuid === parent)
        return parentComment?.replies || []
      },
      staleTime: Infinity,
    },
  )
}

export function useReplie(parent, uuid) {
  const { data: allComments } = useReplies(parent)
  return allComments?.find((comment) => comment.uuid === uuid)
}

export function useComment(uuid) {
  const { data: allComments } = useComments()
  return allComments?.results.find((comment) => comment.uuid === uuid)
}

export function useUsers({ initial, arroba }) {
  const { token } = useSession()
  return useQuery(
    ['GET_USERS', arroba],
    () => getUsersService({ username: arroba, token }),
    {
      enabled: arroba.length >= 4,
      initialData: () => {
        if (arroba.length < 4) {
          return initial
        }
        return []
      },
      cacheTime: Infinity,
    }
  )
}
